/**
 * Utility functions to convert between frontend camelCase and backend snake_case field names
 */

// Convert camelCase to snake_case
export const toSnakeCase = (str: string): string => {
  return str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
};

// Convert snake_case to camelCase
export const toCamelCase = (str: string): string => {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
};

/**
 * Parse and format message content from Krybot API responses.
 * Handles JSON responses by extracting readable text, or formats JSON nicely if no text field exists.
 * Prioritizes mapping.raw which contains the full formatted response from Krybot.
 */
export const parseMessageContent = (content: string | null | undefined): string => {
  if (!content) return '';
  
  const trimmed = content.trim();
  
  // If content is JSON, try to extract a readable message
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    try {
      const parsed = JSON.parse(trimmed);
      // Try to extract message from common JSON structures
      if (typeof parsed === 'object' && parsed !== null) {
        // Priority 1: mapping.raw (contains the full formatted response from Krybot)
        if (parsed.mapping && typeof parsed.mapping === 'object' && parsed.mapping !== null) {
          if (parsed.mapping.raw && typeof parsed.mapping.raw === 'string') {
            return parsed.mapping.raw;
          }
        }
        
        // Priority 2: message field (could be string or object with content)
        if (parsed.message) {
          if (typeof parsed.message === 'string') {
            return parsed.message;
          } else if (typeof parsed.message === 'object' && parsed.message !== null) {
            if (parsed.message.content && typeof parsed.message.content === 'string') {
              return parsed.message.content;
            }
          }
        }
        
        // Priority 3: Other common fields
        if (parsed.content && typeof parsed.content === 'string') {
          return parsed.content;
        } else if (parsed.text && typeof parsed.text === 'string') {
          return parsed.text;
        } else if (parsed.response && typeof parsed.response === 'string') {
          return parsed.response;
        } else if (parsed.answer && typeof parsed.answer === 'string') {
          return parsed.answer;
        } else {
          // If it's an object but no clear message field, format it nicely as JSON
          return JSON.stringify(parsed, null, 2);
        }
      }
    } catch (e) {
      // Not valid JSON, use as-is
      console.warn('Failed to parse message content as JSON:', e);
    }
  }
  
  // Return content as-is if not JSON or if JSON parsing failed
  return content;
};

// Convert object keys from camelCase to snake_case
export const keysToSnakeCase = (obj: Record<string, any>): Record<string, any> => {
  const result: Record<string, any> = {};
  for (const [key, value] of Object.entries(obj)) {
    result[toSnakeCase(key)] = value;
  }
  return result;
};

// Convert object keys from snake_case to camelCase
export const keysToCamelCase = (obj: Record<string, any>): Record<string, any> => {
  const result: Record<string, any> = {};
  for (const [key, value] of Object.entries(obj)) {
    result[toCamelCase(key)] = value;
  }
  return result;
};

// Specific field mappings for ChatThread
export const chatThreadFieldMappings = {
  // Frontend camelCase -> Backend snake_case
  toBackend: {
    isPinned: 'is_pinned',
    messageCount: 'message_count',
    updatedAt: 'updated_at',
    createdAt: 'created_at',
    uiRole: 'ui_role',
    lastMessageId: 'last_message_id',
    userId: 'user_id',
    model: 'model', // Model is already the same in both
    isEmpty: 'is_empty',
    isDefaultNamed: 'is_default_named',
    unreadCount: 'unread_count',
    isOwner: 'is_owner',
  },
  // Backend snake_case -> Frontend camelCase
  toFrontend: {
    is_pinned: 'isPinned',
    message_count: 'messageCount',
    updated_at: 'updatedAt',
    created_at: 'createdAt',
    ui_role: 'uiRole',
    last_message_id: 'lastMessageId',
    user_id: 'userId',
    model: 'model', // Model is already the same in both
    is_empty: 'isEmpty',
    is_default_named: 'isDefaultNamed',
    unread_count: 'unreadCount',
    is_owner: 'isOwner',
  }
};

// Convert ChatThread object for backend API
export const chatThreadToBackend = (thread: Record<string, any>): Record<string, any> => {
  const result = { ...thread };
  if (result.context && typeof result.context === 'object' && !Array.isArray(result.context)) {
    result.context = keysToSnakeCase(result.context as Record<string, any>);
  }

  // Apply specific field mappings
  for (const [frontendKey, backendKey] of Object.entries(chatThreadFieldMappings.toBackend)) {
    if (frontendKey in result) {
      result[backendKey] = result[frontendKey];
      if (frontendKey !== backendKey) {
        delete result[frontendKey];
      }
    }
  }
  
  return result;
};

// Convert ChatThread object from backend API
export const chatThreadFromBackend = (thread: Record<string, any>): any => {
  const result = { ...thread };
  
  // Explicitly preserve the name field (it's the same in both frontend and backend)
  // Check multiple possible locations and field names for the name field
  // Backend may use: name, title, or thread_name (for schema compatibility)
  // If name is "Untitled Thread", try to use description or generate a better name
  let threadName: string | undefined;
  
  // Check name field first (primary field)
  if ('name' in thread && thread.name !== undefined && thread.name !== null && thread.name !== 'Untitled Thread') {
    threadName = thread.name;
  } else if (thread.result && typeof thread.result === 'object' && 'name' in thread.result && thread.result.name !== 'Untitled Thread') {
    threadName = thread.result.name;
  } else if (thread.data && typeof thread.data === 'object' && 'name' in thread.data && thread.data.name !== 'Untitled Thread') {
    threadName = thread.data.name;
  }
  // Check title field (alternative field name)
  else if ('title' in thread && thread.title !== undefined && thread.title !== null && thread.title !== 'Untitled Thread') {
    threadName = thread.title;
  } else if (thread.result && typeof thread.result === 'object' && 'title' in thread.result && thread.result.title !== 'Untitled Thread') {
    threadName = thread.result.title;
  } else if (thread.data && typeof thread.data === 'object' && 'title' in thread.data && thread.data.title !== 'Untitled Thread') {
    threadName = thread.data.title;
  }
  // Check thread_name field (alternative field name)
  else if ('thread_name' in thread && thread.thread_name !== undefined && thread.thread_name !== null && thread.thread_name !== 'Untitled Thread') {
    threadName = thread.thread_name;
  } else if (thread.result && typeof thread.result === 'object' && 'thread_name' in thread.result && thread.result.thread_name !== 'Untitled Thread') {
    threadName = thread.result.thread_name;
  } else if (thread.data && typeof thread.data === 'object' && 'thread_name' in thread.data && thread.data.thread_name !== 'Untitled Thread') {
    threadName = thread.data.thread_name;
  }
  // If name is "Untitled Thread", try metadata/context from Krybot bootstrap
  else if (thread.metadata && typeof thread.metadata === 'object') {
    const meta = thread.metadata as Record<string, unknown>;
    const metaName = typeof meta.name === 'string' ? meta.name.trim() : '';
    if (metaName && metaName !== 'Untitled Thread') {
      threadName = metaName;
    } else {
      const ctx = meta.context;
      if (ctx && typeof ctx === 'object') {
        const surfaceLabel =
          (ctx as Record<string, unknown>).surfaceLabel ??
          (ctx as Record<string, unknown>).surface_label;
        if (typeof surfaceLabel === 'string' && surfaceLabel.trim()) {
          threadName = surfaceLabel.trim();
        }
      }
    }
  } else if (thread.context && typeof thread.context === 'object') {
    const ctx = thread.context as Record<string, unknown>;
    const surfaceLabel = ctx.surfaceLabel ?? ctx.surface_label;
    if (typeof surfaceLabel === 'string' && surfaceLabel.trim()) {
      threadName = surfaceLabel.trim();
    }
  }
  // If name is "Untitled Thread", try to use description as fallback
  else if (thread.description && typeof thread.description === 'string' && thread.description.trim() !== '') {
    // Use description, but truncate if too long
    threadName = thread.description.length > 50 ? thread.description.substring(0, 50) + '...' : thread.description;
  } else if (thread.result && typeof thread.result === 'object' && thread.result.description && typeof thread.result.description === 'string' && thread.result.description.trim() !== '') {
    threadName = thread.result.description.length > 50 ? thread.result.description.substring(0, 50) + '...' : thread.result.description;
  } else if (thread.data && typeof thread.data === 'object' && thread.data.description && typeof thread.data.description === 'string' && thread.data.description.trim() !== '') {
    threadName = thread.data.description.length > 50 ? thread.data.description.substring(0, 50) + '...' : thread.data.description;
  }
  // Fallback to name even if it's "Untitled Thread" (better than nothing)
  else if ('name' in thread && thread.name !== undefined && thread.name !== null) {
    threadName = thread.name;
  } else if (thread.result && typeof thread.result === 'object' && 'name' in thread.result) {
    threadName = thread.result.name;
  } else if (thread.data && typeof thread.data === 'object' && 'name' in thread.data) {
    threadName = thread.data.name;
  }
  
  if (threadName) {
    result.name = threadName;
  } else {
    // Final fallback: generate a descriptive name from thread ID and creation date
    const threadId = thread.id || 'unknown';
    const shortId = threadId.substring(0, 8);
    const createdAt = thread.created_at || thread.result?.created_at || thread.data?.created_at;
    if (createdAt) {
      try {
        const date = new Date(createdAt);
        const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        result.name = `Thread ${shortId} (${dateStr})`;
      } catch (e) {
        result.name = `Thread ${shortId}`;
      }
    } else {
      result.name = `Thread ${shortId}`;
    }
  }
  
  // Apply specific field mappings
  for (const [backendKey, frontendKey] of Object.entries(chatThreadFieldMappings.toFrontend)) {
    if (backendKey in result) {
      result[frontendKey] = result[backendKey];
      if (backendKey !== frontendKey) {
        delete result[backendKey];
      }
    }
  }
  
  // Explicitly ensure isPinned is set correctly (handle both snake_case and camelCase)
  // Check the original thread object first, then the result
  if ('is_pinned' in thread) {
    result.isPinned = thread.is_pinned;
    if ('is_pinned' in result) {
      delete result.is_pinned;
    }
  } else if ('is_pinned' in result) {
    result.isPinned = result.is_pinned;
    delete result.is_pinned;
  } else if ('isPinned' in thread) {
    result.isPinned = thread.isPinned;
  }
  
  // Default to false if not set
  if (result.isPinned === undefined) {
    result.isPinned = false;
  }

  // Explicitly map ui_role (same pattern as isPinned)
  if ('ui_role' in thread && thread.ui_role != null) {
    result.uiRole = thread.ui_role;
    if ('ui_role' in result) {
      delete result.ui_role;
    }
  } else if ('ui_role' in result) {
    result.uiRole = result.ui_role;
    delete result.ui_role;
  } else if ('uiRole' in thread && thread.uiRole != null) {
    result.uiRole = thread.uiRole;
  }
  
  // Debug logging for thread conversion (including name field)
  if (process.env.NODE_ENV === 'development') {
    const hasName = 'name' in thread || 'name' in result;
    const finalThreadName = result.name;
    const originalName = thread.name;
    const hasDescription = 'description' in thread && thread.description;
    
    // Log if name was "Untitled Thread" and we used description or fallback
    if (originalName === 'Untitled Thread' && finalThreadName !== 'Untitled Thread') {
      console.log('✅ [chatThreadFromBackend] Replaced "Untitled Thread" with:', {
        threadId: thread.id,
        originalName,
        finalName: finalThreadName,
        usedDescription: hasDescription && finalThreadName === (thread.description?.substring(0, 50) || thread.description),
        description: thread.description
      });
    } else if (!hasName || !finalThreadName || finalThreadName === 'Untitled Thread') {
      // Only log this warning in development and limit frequency to avoid performance issues
      if (process.env.NODE_ENV === 'development' && Math.random() < 0.1) {
        console.warn('⚠️ [chatThreadFromBackend] Thread missing or has "Untitled Thread" name (sample):', {
          threadId: thread.id,
          threadNameValue: finalThreadName,
          originalName
        });
      }
    }
    // Only log isPinned conversion for a sample of threads to reduce noise
    if (Math.random() < 0.1) {
      console.log('🔍 [chatThreadFromBackend] isPinned conversion:', {
        threadId: thread.id,
        threadName: threadName,
        originalIsPinned: thread.is_pinned,
        originalIsPinnedCamel: thread.isPinned,
        resultIsPinned: result.isPinned,
        hasIsPinned: 'isPinned' in result,
        hasIsPinnedSnake: 'is_pinned' in result,
      });
    }
  }
  
  // Ensure model information is properly structured
  // Backend might return model in different formats (context, metadata, or direct model field)
  if (!result.model && (thread.context || thread.metadata || thread.artifact_context)) {
    const context = thread.context || thread.artifact_context || {};
    const metadata = thread.metadata || {};
    
    // Try to construct model from context or metadata
    if (context.artifact_tree_id || metadata.model?.artifactTreeId || metadata.model?.artifact_tree_id) {
      result.model = {
        type: context.model_type || metadata.model?.type || 'company',
        ...(context.artifact_tree_id && { artifactTreeId: context.artifact_tree_id }),
        ...(metadata.model?.artifactTreeId && { artifactTreeId: metadata.model.artifactTreeId }),
        ...(metadata.model?.artifact_tree_id && { artifactTreeId: metadata.model.artifact_tree_id }),
        ...(context.business_model_id && { businessModelId: context.business_model_id }),
        ...(metadata.model?.businessModelId && { businessModelId: metadata.model.businessModelId }),
        ...(context.prime_artifact_id && { artifactId: context.prime_artifact_id }),
        ...(metadata.model?.artifactId && { artifactId: metadata.model.artifactId }),
      };
    } else if (metadata.model) {
      // Use model directly from metadata
      result.model = metadata.model;
    }
  }
  
  // Debug logging for thread model information
  if (process.env.NODE_ENV === 'development' && Math.random() < 0.1) { // Log 10% of the time
    console.log('🔍 [chatThreadFromBackend] Thread model conversion:', { 
      threadId: thread.id,
      threadName: thread.name,
      hasModel: !!result.model,
      modelType: result.model?.type,
      artifactTreeId: result.model?.artifactTreeId,
      backendContext: thread.context || thread.artifact_context,
      backendMetadata: thread.metadata
    });
  }

  if (result.context && typeof result.context === 'object' && !Array.isArray(result.context)) {
    result.context = keysToCamelCase(result.context as Record<string, any>);
  }

  // Merge artifact_context (bootstrap / Krybot thread payload) into frontend context
  const artifactCtx = thread.artifact_context;
  if (artifactCtx && typeof artifactCtx === 'object' && !Array.isArray(artifactCtx)) {
    const normalized = keysToCamelCase(artifactCtx as Record<string, any>);
    result.context = { ...(result.context || {}), ...normalized };
  }
  
  return result;
};
